import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix } from '../../shared/utils/brandCurrency';
import {
  formatComptabiliteMoney,
  formatComptabiliteDate,
  formatComptabiliteNumber,
  getComptabiliteDatePickerLocale } from './utils/comptabiliteSaudiFormat';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Book, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, LayoutGrid, Layers, TrendingUp, TrendingDown, DollarSign, FileText, List, LayoutList, Link } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { useComptabiliteTableColumnVisibility, useComptabiliteListRowSelection, ComptabiliteBulkDeleteFilterAction, COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS } from './utils/comptabiliteListPageUtils';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  getGrandLivreEntries,
  createGrandLivreEntry,
  updateGrandLivreEntry,
  deleteGrandLivreEntry,
  getChartOfAccounts,
} from '../../shared/utils/comptabiliteService';
import { buildChartAccountOptions, CHART_ACCOUNTS_REFRESH_EVENT } from './utils/chartAccountOptions';
import { fetchCurrentFiscalYearContext, resolveFiscalYearLabel } from './utils/fiscalYearUtils';
import { useComptabiliteConfig } from './useComptabiliteConfig';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import '../../core/styles/global-dropdowns.css';
import {
  EMPTY_GRAND_LIVRE_FORM,
  buildGrandLivrePayload,
  recordToFormData } from './04-grand-livre/grandLivrePayload';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { useSettings } from '../../core/contexts/SettingsContext';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import ComptabiliteFiscalYearField from './components/ComptabiliteFiscalYearField';

registerLocale('ar', arLocale);

const GRAND_LIVRE_LIST_COLUMNS = [
  { key: 'grand_livre_ref', labelKey: 'th_reference' },
  { key: 'numero_compte', labelKey: 'th_account' },
  { key: 'libelle', labelKey: 'th_label' },
  { key: 'date_ecriture', labelKey: 'th_date' },
  { key: 'montant_debit', labelKey: 'th_debit' },
  { key: 'montant_credit', labelKey: 'th_credit' },
  { key: 'validation_ecriture', labelKey: 'th_validation' },
];

const GrandLivrePage = () => {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  useComptabiliteConfig();
  const { isDashboardLightMode } = useGlobalTheme();
  const p = (key, opts) => t(`modules.comptabilite.pages.grandLivrePage.${key}`, opts);
  const datePickerLocale = getComptabiliteDatePickerLocale();
  const fmtMoney = (amount) => formatComptabiliteMoney(amount);
  const fmtNum = (amount) => formatComptabiliteNumber(amount, i18n.language);
  const fmtDate = (value) => formatComptabiliteDate(value, i18n.language);
  const fmtMonthYear = (value) => formatComptabiliteDate(value, i18n.language, { monthYear: true });

  const translateValidation = useCallback((status) => {
    const map = {
      'Validé': p('validation_validated'),
      'Non Validé': p('validation_not_validated'),
      'En Attente': p('validation_pending') };
    return map[status] || status || p('validation_not_validated');
  }, [t]);

  const translateBalanceSense = useCallback((sense) => {
    const map = {
      'Débiteur': p('sense_debit'),
      'Créditeur': p('sense_credit'),
      'Nul': p('sense_null') };
    return map[sense] || sense || p('not_available');
  }, [t]);

  const translateEditionType = useCallback((type) => {
    const map = {
      'Complet': p('edition_complete'),
      'Par Compte': p('edition_by_account'),
      'Par Classe': p('edition_by_class'),
      'Par Période': p('edition_by_period') };
    return map[type] || type || p('not_available');
  }, [t]);

  const [searchTerm, setSearchTerm] = useState('');
  const [entries, setEntries] = useState([]);
  const [chartAccounts, setChartAccounts] = useState([]);
  const [accountFilter, setAccountFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  const [grandLivreFormTab, setGrandLivreFormTab] = useState('ecritureInfo');

  const grandLivreFormTabs = useMemo(() => ([
    { id: 'ecritureInfo', label: p('section_entry_info'), icon: Book },
    { id: 'montantsSoldes', label: p('section_amounts_balances'), icon: DollarSign },
    { id: 'validationPieces', label: p('section_documents_validation'), icon: FileText }
  ]), [p]);
  
  const [formData, setFormData] = useState({ ...EMPTY_GRAND_LIVRE_FORM, grand_livre_ref: '' });
  const [fiscalYearLabel, setFiscalYearLabel] = useState('');
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [setDateRange] = useState({ start: null, end: null });
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_entries');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  const { filters: columnFilters, setFilter: setColumnFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const columnFilterRules = useMemo(() => [
    textColumnRule('grand_livre_ref', (row) => row.grand_livre_ref ?? ''),
    textColumnRule('numero_compte', (row) => row.numero_compte ?? ''),
    textColumnRule('date_ecriture', (row) => row.date_ecriture ?? ''),
    textColumnRule('montant_debit', (row) => String(row.montant_debit ?? '')),
    textColumnRule('montant_credit', (row) => String(row.montant_credit ?? '')),
  ], []);

  useEffect(() => {
    loadEntries();
    loadChartAccounts();
    fetchCurrentFiscalYearContext()
      .then(({ label }) => { if (label) setFiscalYearLabel(label); })
      .catch(() => {});
  }, []);

  const loadChartAccounts = async () => {
    try {
      const rows = await getChartOfAccounts();
      setChartAccounts(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Error loading chart accounts:', error);
      setChartAccounts([]);
    }
  };

  useEffect(() => {
    const onRefresh = () => { void loadChartAccounts(); };
    window.addEventListener(CHART_ACCOUNTS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(CHART_ACCOUNTS_REFRESH_EVENT, onRefresh);
  }, []);

  const accountOptions = useMemo(
    () => buildChartAccountOptions(chartAccounts),
    [chartAccounts],
  );

  const handleAccountNumberChange = useCallback((numero) => {
    const acc = chartAccounts.find((a) => a.account_number === numero);
    setFormData((prev) => ({
      ...prev,
      numero_compte: numero,
      libelle_compte: acc?.account_name || prev.libelle_compte,
    }));
  }, [chartAccounts]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadEntries = async () => {
    try {
      const response = await getGrandLivreEntries();
      setEntries(sortRecordsNewestFirst(Array.isArray(response) ? response : []));
    } catch (error) {
      console.error('Error loading grand livre:', error);
      setEntries([]);
    }
  };

  const stats = {
    total: entries.length,
    totalDebit: fmtNum(entries.reduce((sum, e) => sum + (parseFloat(e.montant_debit) || 0), 0)),
    totalCredit: fmtNum(entries.reduce((sum, e) => sum + (parseFloat(e.montant_credit) || 0), 0)),
    lettrees: entries.filter(e => e.lettrage_code).length,
    aRapprocher: entries.filter(e => e.rapprochement_bancaire === 'Non Rapproché').length
  };

  const mainTabs = useMemo(() => [
    { id: 'all_entries', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_compte', name: p('tab_by_account'), icon: Book },
    { id: 'par_journal', name: p('tab_by_journal'), icon: FileText }
  ], [t]);

  const quickFilterButtons = useMemo(() => [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'valide', name: p('filter_validated'), icon: CheckCircle },
    { id: 'non_valide', name: p('filter_not_validated'), icon: AlertCircle }
  ], [t]);

  const buildFieldDistribution = useCallback((field, colors, limit = 6) => {
    const counts = {};
    entries.forEach((entry) => {
      const key = String(entry[field] || '').trim() || p('not_available');
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([value, count], index) => ({
        filterValue: value,
        name: value,
        count,
        color: colors[index % colors.length],
      }));
  }, [entries, p]);

  const distributionInfo = useMemo(() => {
    const byValidation = {
      title: p('dist_entries_overview'),
      data: [
        { filterValue: 'Validé', name: p('dist_validated'), count: entries.filter(e => e.validation_ecriture === 'Validé').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterValue: 'Non Validé', name: p('dist_not_validated'), count: entries.filter(e => e.validation_ecriture === 'Non Validé').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterValue: 'En Attente', name: p('dist_pending'), count: entries.filter(e => e.validation_ecriture === 'En Attente').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterValue: '__matched__', name: p('dist_matched'), count: stats.lettrees, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      ],
    };
    const byAccount = {
      title: p('tab_by_account'),
      data: buildFieldDistribution('numero_compte', [
        'bg-gradient-to-r from-indigo-500 to-indigo-600',
        'bg-gradient-to-r from-blue-500 to-blue-600',
        'bg-gradient-to-r from-cyan-500 to-cyan-600',
        'bg-gradient-to-r from-teal-500 to-teal-600',
        'bg-gradient-to-r from-emerald-500 to-emerald-600',
        'bg-gradient-to-r from-green-500 to-green-600',
      ]),
    };
    const byJournal = {
      title: p('tab_by_journal'),
      data: buildFieldDistribution('journal_code', [
        'bg-gradient-to-r from-amber-500 to-amber-600',
        'bg-gradient-to-r from-orange-500 to-orange-600',
        'bg-gradient-to-r from-rose-500 to-rose-600',
        'bg-gradient-to-r from-purple-500 to-purple-600',
        'bg-gradient-to-r from-violet-500 to-violet-600',
        'bg-gradient-to-r from-fuchsia-500 to-fuchsia-600',
      ]),
    };
    if (mainViewMode === 'par_compte') return byAccount;
    if (mainViewMode === 'par_journal') return byJournal;
    return byValidation;
  }, [entries, stats.lettrees, mainViewMode, p, buildFieldDistribution]);

  const statCards = useMemo(() => [
    { title: p('stat_total_entries'), value: stats.total, icon: Book, color: 'from-indigo-500 to-indigo-700' },
    { title: p('stat_total_debit'), value: fmtMoney(entries.reduce((sum, e) => sum + (parseFloat(e.montant_debit) || 0), 0)), icon: TrendingUp, color: 'from-green-500 to-green-600' },
    { title: p('stat_total_credit'), value: fmtMoney(entries.reduce((sum, e) => sum + (parseFloat(e.montant_credit) || 0), 0)), icon: TrendingDown, color: 'from-blue-500 to-blue-600' },
    { title: p('stat_matched'), value: stats.lettrees, icon: CheckCircle, color: 'from-emerald-500 to-emerald-600' },
    { title: p('stat_to_reconcile'), value: stats.aRapprocher, icon: AlertCircle, color: 'from-amber-500 to-amber-600' }
  ], [stats, t]);

  const editionTypeOptions = useMemo(() => [
    { value: 'Complet', label: p('edition_complete') },
    { value: 'Par Compte', label: p('edition_by_account') },
    { value: 'Par Classe', label: p('edition_by_class') },
    { value: 'Par Période', label: p('edition_by_period') }
  ], [t]);

  const senseOptions = useMemo(() => [
    { value: 'Débiteur', label: p('sense_debit') },
    { value: 'Créditeur', label: p('sense_credit') },
    { value: 'Nul', label: p('sense_null') }
  ], [t]);

  const validationOptions = useMemo(() => [
    { value: 'Validé', label: p('validation_validated') },
    { value: 'Non Validé', label: p('validation_not_validated') },
    { value: 'En Attente', label: p('validation_pending') }
  ], [t]);

  const filteredEntries = useMemo(() => {
    const base = entries.filter(e => {

      if (searchTerm && !e.grand_livre_ref?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !e.numero_compte?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !e.libelle_compte?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !e.libelle_ecriture?.toLowerCase().includes(searchTerm.toLowerCase())) return false;

      if (accountFilter && e.numero_compte !== accountFilter) return false;

      if (mainViewMode === 'all_entries') {
        if (quickFilter === 'valide' && e.validation_ecriture !== 'Validé') return false;
        if (quickFilter === 'non_valide' && e.validation_ecriture !== 'Non Validé') return false;
      }

      if (activeDistributionFilter) {
        if (mainViewMode === 'par_compte') {
          const accountKey = String(e.numero_compte || '').trim() || p('not_available');
          if (accountKey !== activeDistributionFilter) return false;
        } else if (mainViewMode === 'par_journal') {
          const journalKey = String(e.journal_code || '').trim() || p('not_available');
          if (journalKey !== activeDistributionFilter) return false;
        } else if (activeDistributionFilter === '__matched__') {
          if (!e.lettrage_code) return false;
        } else if (e.validation_ecriture !== activeDistributionFilter) {
          return false;
        }
      }

      return true;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [entries, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, columnFilters, columnFilterRules, p, accountFilter]);

  const {
    selectedRecords: selectedRecords,
    setSelectedRecords: setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord: handleSelectRecordBase,
    clearSelection,
  } = useComptabiliteListRowSelection(filteredEntries, (row) => row.id);
  const handleSelectRecord = handleSelectRecordBase;
  
  

  const {
    qawaemColumnDefs,
    handleToggleColumn,
    qawaemVisibleColumnCount,
    isColVisible,
    visibleDataColumnCount } = useComptabiliteTableColumnVisibility(GRAND_LIVRE_LIST_COLUMNS, p);
  const grandLivreTableColSpan = visibleDataColumnCount + 2;

  const generateGrandLivreReference = useCallback(() => {
    const year = new Date().getFullYear();
    const prefix = getEntityPrefix(settings, 'grandLivre', 'GRLV');
    return generateEntityReference(`${prefix}-${year}`, entries, (row) => row.grand_livre_ref);
  }, [entries, settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = buildGrandLivrePayload(formData);
      if (editingRecord) {
        await updateGrandLivreEntry(editingRecord.id, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'قيد الدفتر العام' }));
      } else {
        await createGrandLivreEntry(submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'قيد الدفتر العام' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error', { message: error.message }));
    } finally {
      resetForm();
      loadEntries();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ ...recordToFormData(record), grand_livre_ref: record.grand_livre_ref || '' });
    setGrandLivreFormTab('ecritureInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await deleteGrandLivreEntry(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'قيد الدفتر العام' }));
        loadEntries();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('save_error', { message: error.message }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => deleteGrandLivreEntry(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'قيود الدفتر العام' }));
        setSelectedRecords([]);
        clearSelection();
        loadEntries();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('save_error', { message: error.message }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_GRAND_LIVRE_FORM, grand_livre_ref: '' });
    setEditingRecord(null);
    setShowModal(false);
    setGrandLivreFormTab('ecritureInfo');
  };

  const openNewGrandLivreModal = () => {
    setEditingRecord(null);
    setFormData({
      ...EMPTY_GRAND_LIVRE_FORM,
      grand_livre_ref: generateGrandLivreReference(),
      exercice_fiscal: fiscalYearLabel || resolveFiscalYearLabel(),
    });
    setGrandLivreFormTab('ecritureInfo');
    setShowModal(true);
  };

  return (
    <div className={`space-y-3 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <ComptabiliteBulkDeleteFilterAction
              count={selectedRecords.length}
              onClick={handleBulkDelete}
              label={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
              title={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
            />
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
            <FacturationViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLight={isDashboardLightMode}
              cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
              tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
            />
          </div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Book className="h-8 w-8 text-indigo-400" />
            {p('title')}
            <button onClick={loadEntries} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}>
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{p('advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button className="bg-gradient-to-r from-indigo-500 to-indigo-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-indigo-600 hover:to-indigo-800 transition-all duration-300 flex items-center space-x-2" onClick={openNewGrandLivreModal}>
            <Plus className="h-5 w-5" /><span>{p('new_entry')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_entries' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-4 border border-white/20 max-w-md">
            <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('filter_by_account', { defaultValue: 'تصفية حسب الحساب' })}</h3>
            <CustomSelect
              value={accountFilter}
              onChange={setAccountFilter}
              options={[{ value: '', label: p('filter_all') }, ...accountOptions]}
              placeholder={p('placeholder_account_number')}
              searchable
              autoSelect={false}
            />
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter ? (
                <button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <X size={14} />{p('reset_filter', { defaultValue: 'إعادة التعيين' })}
                </button>
              ) : null}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button
                  key={`${item.filterValue}-${item.name}`}
                  type="button"
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterValue ? null : item.filterValue)}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left w-full ${activeDistributionFilter === item.filterValue ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterValue ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterValue ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColVisible('grand_livre_ref') ? (
                    <TableColumnHeaderFilter columnKey="grand_livre_ref" label={p('th_reference')} value={columnFilters.grand_livre_ref || ''} onChange={(v) => setColumnFilter('grand_livre_ref', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('numero_compte') ? (
                    <TableColumnHeaderFilter columnKey="numero_compte" label={p('th_account')} value={columnFilters.numero_compte || ''} onChange={(v) => setColumnFilter('numero_compte', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('libelle') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('th_label')}</th>
                  ) : null}
                  {isColVisible('date_ecriture') ? (
                    <TableColumnHeaderFilter columnKey="date_ecriture" label={p('th_date')} value={columnFilters.date_ecriture || ''} onChange={(v) => setColumnFilter('date_ecriture', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('montant_debit') ? (
                    <TableColumnHeaderFilter columnKey="montant_debit" label={p('th_debit')} value={columnFilters.montant_debit || ''} onChange={(v) => setColumnFilter('montant_debit', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white text-right" />
                  ) : null}
                  {isColVisible('montant_credit') ? (
                    <TableColumnHeaderFilter columnKey="montant_credit" label={p('th_credit')} value={columnFilters.montant_credit || ''} onChange={(v) => setColumnFilter('montant_credit', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white text-right" />
                  ) : null}
                  {isColVisible('validation_ecriture') ? (
                    <th className="px-6 py-4 text-center text-white font-semibold">{p('th_validation')}</th>
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('th_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  thClassName={COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredEntries.length > 0 ? (
                  filteredEntries.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                            />
                          </div>
                        </td>
                      {isColVisible('grand_livre_ref') ? (
                      <td className="px-6 py-4"><span className="text-white font-semibold">{record.grand_livre_ref}</span></td>
                      ) : null}
                      {isColVisible('numero_compte') ? (
                      <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{record.numero_compte}</span><span className="text-white/50 text-xs mt-0.5">{record.journal_code || 'N/A'}</span></div></td>
                      ) : null}
                      {isColVisible('libelle') ? (
                      <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.libelle_ecriture || record.libelle_compte}</span></td>
                      ) : null}
                      {isColVisible('date_ecriture') ? (
                      <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.date_ecriture ? fmtDate(record.date_ecriture) : '-'}</span></td>
                      ) : null}
                      {isColVisible('montant_debit') ? (
                      <td className="px-6 py-4 text-right"><span className="text-green-400 font-semibold">{fmtMoney(record.montant_debit || 0)}</span></td>
                      ) : null}
                      {isColVisible('montant_credit') ? (
                      <td className="px-6 py-4 text-right"><span className="text-blue-400 font-semibold">{fmtMoney(record.montant_credit || 0)}</span></td>
                      ) : null}
                      {isColVisible('validation_ecriture') ? (
                      <td className="px-6 py-4 text-center">
                        <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                          record.validation_ecriture === 'Validé' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                          record.validation_ecriture === 'En Attente' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                          'bg-orange-500/20 text-orange-400 border-orange-500/30'
                        }`}>
                          {translateValidation(record.validation_ecriture)}
                        </span>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => handleDelete(record.id)}
                          />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={grandLivreTableColSpan} className="text-center py-12 text-white/60"><Book size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_title')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredEntries.length > 0 ? (
              filteredEntries.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><Book size={20} className="text-indigo-400" />{record.grand_livre_ref}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          record.validation_ecriture === 'Validé' ? 'bg-green-500/20 text-green-300' :
                          'bg-orange-500/20 text-orange-300'
                        }`}>{translateValidation(record.validation_ecriture)}</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_account')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_number')}</span> {record.numero_compte}</div>
                            <div><span className="font-medium">{p('label_label')}</span> {record.libelle_compte}</div>
                            <div><span className="font-medium">{p('label_class')}</span> {record.compte_classe || p('not_available')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_entry')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_entry_date')}:</span> {record.date_ecriture ? fmtDate(record.date_ecriture) : p('not_available')}</div>
                            <div><span className="font-medium">{p('label_document')}</span> {record.numero_piece || p('not_available')}</div>
                            <div><span className="font-medium">{p('label_journal')}</span> {record.journal_code || p('not_available')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_amounts')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium text-green-400">{p('label_debit')}:</span> <span className="text-white font-bold">{fmtMoney(record.montant_debit || 0)}</span></div>
                            <div><span className="font-medium text-blue-400">{p('label_credit')}:</span> <span className="text-white font-bold">{fmtMoney(record.montant_credit || 0)}</span></div>
                            <div><span className="font-medium">{p('label_sense')}:</span> {translateBalanceSense(record.sens_solde) || p('not_available')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_status')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_validation')}:</span> {translateValidation(record.validation_ecriture)}</div>
                            <div><span className="font-medium">{p('view_label_matching')}:</span> {record.lettrage_code || p('not_matched')}</div>
                            <div><span className="font-medium">{p('view_label_reconciliation')}:</span> {record.rapprochement_bancaire || p('not_reconciled')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><Book size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_title')}</p></div>
            )}
          </div>
        )}
      </div>

      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
          onClick={() => setShowViewModal(false)}
          role="presentation"
        >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_modal_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Book size={18} className="text-indigo-400" />{p('section_general_info')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-white/60 text-sm mb-1">{p('view_label_reference')}</p><p className="text-white font-medium">{viewingRecord.grand_livre_ref}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('view_label_fiscal_year')}</p><p className="text-white font-medium">{viewingRecord.exercice_fiscal}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('view_label_edition_type')}</p><p className="text-white font-medium">{translateEditionType(viewingRecord.type_edition) || p('not_available')}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('view_label_account_number')}</p><p className="text-white font-medium">{viewingRecord.numero_compte}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('view_label_account_label')}</p><p className="text-white font-medium">{viewingRecord.libelle_compte}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_entry_date')}</p><p className="text-white font-medium">{viewingRecord.date_ecriture ? fmtDate(viewingRecord.date_ecriture) : p('not_available')}</p></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_amounts')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{p('view_label_debit')}</p><p className="text-green-400 font-bold text-xl">{fmtMoney(viewingRecord.montant_debit || 0)}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('view_label_credit')}</p><p className="text-blue-400 font-bold text-xl">{fmtMoney(viewingRecord.montant_credit || 0)}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('view_label_balance_sense')}</p><p className="text-white font-medium">{translateBalanceSense(viewingRecord.sens_solde) || p('not_available')}</p></div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_validation_matching')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{p('view_label_validation')}</p><p className="text-white font-medium">{translateValidation(viewingRecord.validation_ecriture)}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('view_label_matching')}</p><p className="text-white font-medium">{viewingRecord.lettrage_code || p('not_matched')}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('view_label_reconciliation')}</p><p className="text-white font-medium">{viewingRecord.rapprochement_bancaire || p('not_reconciled')}</p></div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
        </div>,
        document.body
      )}

      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{editingRecord ? p('modal_edit_title') : p('modal_add_title')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="grand-livre-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{editingRecord ? p('update') : p('create')}</button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}><X size={24} /></button>
              </div>
            </div>
            <div className={`shrink-0 px-6 pt-3 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <div className="flex flex-wrap gap-1" role="tablist" aria-label={editingRecord ? p('modal_edit_title') : p('modal_add_title')}>
                {grandLivreFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = grandLivreFormTab === tab.id;
                  return (
                    <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setGrandLivreFormTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? (isDashboardLightMode ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px' : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px') : (isDashboardLightMode ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80' : 'text-white/55 hover:text-white hover:bg-white/5')}`}>
                      <TabIcon size={16} className={active ? (tab.id === 'montantsSoldes' ? 'text-amber-400' : tab.id === 'lettrageRapprochement' ? 'text-emerald-400' : tab.id === 'piecesValidation' ? 'text-violet-400' : 'text-sky-400') : ''} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="grand-livre-form" onSubmit={handleSubmit}>
                {grandLivreFormTab === 'ecritureInfo' && (
<div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('label_reference')}</label><input type="text" value={formData.grand_livre_ref} onChange={(e) => setFormData({ ...formData, grand_livre_ref: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white font-mono placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_reference')} /></div>
                    <div className="min-w-0"><ComptabiliteFiscalYearField label={p('label_fiscal_year')} value={formData.exercice_fiscal} onChange={(val) => setFormData({ ...formData, exercice_fiscal: val })} placeholder={p('placeholder_fiscal_year')} required labelClassName={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`} /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('label_edition_type')}</label><CustomSelect value={formData.type_edition} onChange={(value) => setFormData({ ...formData, type_edition: value })} options={editionTypeOptions} searchable={false} /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('label_entry_date')}</label><input type="date" required value={formData.date_ecriture} onChange={(e) => setFormData({ ...formData, date_ecriture: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('label_account_number_form')}</label><CustomSelect value={formData.numero_compte} onChange={handleAccountNumberChange} options={accountOptions} placeholder={p('placeholder_account_number')} searchable autoSelect={false} /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('label_account_label')}</label><input type="text" required value={formData.libelle_compte} onChange={(e) => setFormData({ ...formData, libelle_compte: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_account_label')} /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('label_document_number')}</label><input type="text" value={formData.numero_piece} onChange={(e) => setFormData({ ...formData, numero_piece: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_document')} /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('label_journal_code')}</label><input type="text" value={formData.journal_code} onChange={(e) => setFormData({ ...formData, journal_code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_journal')} /></div>
                    <div className="min-w-0 md:col-span-3"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('label_entry_label')}<span className="qawaem-required-mark"> *</span></label><textarea required value={formData.libelle_ecriture} onChange={(e) => setFormData({ ...formData, libelle_ecriture: e.target.value })} rows={2} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_entry_description')} /></div>
                  </div>
                </div>
                )}
                {grandLivreFormTab === 'montantsSoldes' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('label_debit_amount')}</label><CustomNumberInput step={0.01} value={formData.montant_debit || ''} onChange={(value) => setFormData({ ...formData, montant_debit: value })} placeholder={p('placeholder_amount')} /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('label_credit_amount')}</label><CustomNumberInput step={0.01} value={formData.montant_credit || ''} onChange={(value) => setFormData({ ...formData, montant_credit: value })} placeholder={p('placeholder_amount')} /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('label_balance_sense')}</label><CustomSelect value={formData.sens_solde} onChange={(value) => setFormData({ ...formData, sens_solde: value })} options={senseOptions} searchable={false} /></div>
                  </div>
                )}
                {grandLivreFormTab === 'validationPieces' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('label_matching_code')}</label><input type="text" value={formData.lettrage_code} onChange={(e) => setFormData({ ...formData, lettrage_code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_matching')} /></div>
                      <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('label_entry_validation')}</label><CustomSelect value={formData.validation_ecriture} onChange={(value) => setFormData({ ...formData, validation_ecriture: value })} options={[{ value: 'Validé', label: p('validation_validated') }, { value: 'Non Validé', label: p('validation_not_validated') }, { value: 'En Attente', label: p('validation_pending') }]} searchable={false} /></div>
                    </div>
                    <div className="pt-6 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('label_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_notes')} /></div>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default GrandLivrePage;


